import React from "react";
import { useParams, Link } from "react-router-dom";
import SpotCarLogo from "@/components/SpotCarLogo";
import { ExternalLink, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api_endpoints } from "@/lib/api";
import { fmtCurrency, fmtMileage } from "@/lib/format";
import {
    VerdictBadge,
    Card,
    Eyebrow,
    EmptyState,
    Btn,
} from "@/components/app/ui";

export default function SharedCollection() {
    const { t } = useTranslation();
    const { token } = useParams();
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        api_endpoints
            .sharedView(token)
            .then((r) => setData(r))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [token]);

    return (
        <div className="min-h-screen bg-white font-body">
            {/* Public header */}
            <header className="border-b-2 border-black bg-white sticky top-0 z-40">
                <div className="max-w-[1200px] mx-auto flex items-stretch justify-between">
                    <a
                        href="/"
                        className="flex items-center gap-3 border-r-2 border-black px-6 py-4 hover:bg-black hover:text-white transition-colors"
                    >
                        <SpotCarLogo size={32} />
                        <span className="font-display text-xl uppercase tracking-tighter">
                            SpotCar
                        </span>
                    </a>
                    <div className="hidden sm:flex items-center px-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        {t("shared.publicHeader")}
                    </div>
                    <Btn
                        href="/"
                        variant="primary"
                        className="border-l-2 border-r-0 border-y-0 shadow-none"
                    >
                        {t("shared.trySpotCar")}
                    </Btn>
                </div>
            </header>

            <div className="max-w-[1200px] mx-auto p-6 md:p-10">
                {loading ? (
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                        {t("common.loading")}
                    </div>
                ) : error ? (
                    <EmptyState
                        icon={Layers}
                        title={t("shared.notFound")}
                        description={t("shared.notFoundDescription")}
                    />
                ) : (
                    <>
                        <div className="border-b-2 border-black pb-6 mb-6">
                            <Eyebrow>{t("shared.sharedCollection")}</Eyebrow>
                            <h1
                                className="mt-2 font-display text-4xl md:text-6xl uppercase tracking-tight leading-none"
                                data-testid="shared-collection-name"
                            >
                                {data.name}
                            </h1>
                            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                                {t("shared.readOnly", { count: data.analyses?.length || 0 })}
                            </p>
                        </div>

                        {(!data.analyses || data.analyses.length === 0) ? (
                            <EmptyState title={t("shared.noCars")} />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black">
                                {data.analyses.map((a) => (
                                    <Card
                                        key={a.id}
                                        className="p-5 hover:bg-[var(--surface-container-high)] transition-colors"
                                        testid={`shared-card-${a.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <VerdictBadge verdict={a.verdict} size="sm" />
                                            <span className="font-display text-3xl leading-none">
                                                {a.score}
                                            </span>
                                        </div>
                                        <Link
                                            to={`/shared/${token}/${a.id}`}
                                            data-testid={`shared-open-${a.id}`}
                                            className="block mt-4 font-display text-xl uppercase tracking-tight hover:underline"
                                        >
                                            {a.make} {a.model}
                                        </Link>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                            {a.year} · {a.fuelType} · {a.transmission}
                                        </div>
                                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <Eyebrow>{t("shared.price")}</Eyebrow>
                                                <div className="font-display text-lg leading-none mt-1">
                                                    {fmtCurrency(a.price, a.currency)}
                                                </div>
                                            </div>
                                            <div>
                                                <Eyebrow>{t("shared.mileage")}</Eyebrow>
                                                <div className="font-mono text-sm mt-1">
                                                    {fmtMileage(a.mileage, a.mileageUnit)}
                                                </div>
                                            </div>
                                        </div>
                                        {a.notes && (
                                            <div className="mt-4 border-t-2 border-black pt-3 font-mono text-[11px] text-zinc-700 leading-relaxed">
                                                <span className="text-zinc-400">Note · </span>
                                                {a.notes}
                                            </div>
                                        )}
                                        <Link
                                            to={`/shared/${token}/${a.id}`}
                                            className="mt-4 block text-center border-2 border-black bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-black hover:text-white"
                                        >
                                            {t("shared.openReport")}
                                        </Link>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
